/** Fees de Torneos123 que deben los organizadores (Eduardo 2026-10-21). */
import { API_BASE_URL, apiFetch } from "./api";
import { resolveLiveToken } from "./sessionToken";

export type FeeEstado = "en_curso" | "al_corriente" | "pendiente" | "vencido";

export type FeeTournamentRow = {
  tournament_id: number | null;
  tournament_name: string;
  closed: boolean;
  closed_at: string | null;
  due_at: string | null;
  orders_charged: number;
  fee_amount: string;
  charges: string;
  credits: string;
  total: string;
  paid: string;
  saldo: string;
  estado: FeeEstado;
  becas_pending: number;
  becas_approved: number;
};

export type FeeMovement = {
  kind: string;
  label?: string;
  date: string | null;
  tournament_id: number | null;
  tournament_name: string | null;
  detail: string;
  amount: string | null;
};

export type FeeBeca = {
  charge_id: number;
  order_id: number | null;
  tournament_id: number | null;
  tournament_name: string;
  player_name: string | null;
  amount: string;
  status: "pending" | "approved" | "rejected";
  reason: string | null;
  marked_at: string | null;
  reviewed_at: string | null;
  review_note: string | null;
};

export type FeeControl = {
  blocked: boolean;
  blocked_reason: string | null;
  blocked_at: string | null;
  exception_available: boolean;
  exception_reason: string | null;
};

export type FeeLedger = {
  organizer_user_id: number;
  currency: string;
  charges: string;
  credits: string;
  owed: string;
  paid: string;
  adjustments: string;
  saldo: string;
  saldo_closed: string;
  saldo_overdue: string;
  saldo_en_curso: string;
  estado: FeeEstado;
  last_payment: { amount: string; date: string | null; method: string | null } | null;
  tournaments: FeeTournamentRow[];
  movements: FeeMovement[];
  becas: FeeBeca[];
  control: FeeControl;
  due_days: number;
  pay_enabled?: boolean;
  organizer_name?: string;
  responsible_name?: string;
  email?: string | null;
};

export type FeeOrganizerRow = {
  organizer_user_id: number;
  organizer_name: string;
  responsible_name: string;
  email: string | null;
  tournaments: number;
  saldo: string;
  saldo_closed: string;
  saldo_overdue: string;
  estado: FeeEstado;
  last_payment: { amount: string; date: string | null } | null;
  blocked: boolean;
  becas_pending: number;
};

export type FeePaymentReview = { event_id: string; event_type: string; reason: string; date: string | null };

export type FeeOrganizerList = {
  rows: FeeOrganizerRow[];
  payment_reviews?: FeePaymentReview[];
  totals: { por_cobrar: string; vencido: string; cobrado_mes: string; becas_pending: number };
};

type Ok<T> = { ok: boolean; data: T };

export const organizerFeesApi = {
  mine: (token: string) => apiFetch<Ok<FeeLedger>>("/admin/organizer-fees", { token }),
  checkout: (token: string, tournamentId: number | null) =>
    apiFetch<Ok<{ checkout_url: string; amount: string }>>("/admin/organizer-fees/checkout", {
      method: "POST",
      token,
      body: { tournament_id: tournamentId },
    }),
  markBeca: (token: string, tournamentId: number, orderId: number, reason: string) =>
    apiFetch<Ok<{ charge_id: number; beca_status: string }>>(
      `/admin/tournaments/${tournamentId}/registrations/orders/${orderId}/beca`,
      { method: "POST", token, body: { reason } },
    ),
  list: (token: string, params: { filter?: string; q?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.filter && params.filter !== "all") query.set("filter", params.filter);
    if (params.q) query.set("q", params.q);
    const qs = query.toString();
    return apiFetch<Ok<FeeOrganizerList>>(`/superadmin/organizer-fees${qs ? `?${qs}` : ""}`, { token });
  },
  downloadXlsx: (token: string, params: { filter?: string; q?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.filter && params.filter !== "all") query.set("filter", params.filter);
    if (params.q) query.set("q", params.q);
    const qs = query.toString();
    return fetch(`${API_BASE_URL}/superadmin/organizer-fees/export.xlsx${qs ? `?${qs}` : ""}`, {
      headers: { Authorization: `Bearer ${resolveLiveToken(token) ?? token}` },
    });
  },
  recalculate: (token: string) =>
    apiFetch<Ok<FeeOrganizerList>>(`/superadmin/organizer-fees/recalculate`, { method: "POST", token }),
  detail: (token: string, organizerId: number) =>
    apiFetch<Ok<FeeLedger>>(`/superadmin/organizer-fees/${organizerId}`, { token }),
  registerPayment: (
    token: string,
    organizerId: number,
    body: { amount: string; reference: string; tournament_id?: number | null; note?: string | null },
  ) => apiFetch<Ok<FeeLedger>>(`/superadmin/organizer-fees/${organizerId}/payments`, { method: "POST", token, body }),
  adjust: (token: string, organizerId: number, body: { amount: string; reason: string; tournament_id?: number | null }) =>
    apiFetch<Ok<FeeLedger>>(`/superadmin/organizer-fees/${organizerId}/adjustments`, { method: "POST", token, body }),
  setBlock: (token: string, organizerId: number, body: { blocked: boolean; reason?: string | null }) =>
    apiFetch<Ok<FeeLedger>>(`/superadmin/organizer-fees/${organizerId}/block`, { method: "POST", token, body }),
  grantException: (token: string, organizerId: number, reason: string) =>
    apiFetch<Ok<FeeLedger>>(`/superadmin/organizer-fees/${organizerId}/publish-exception`, {
      method: "POST",
      token,
      body: { reason },
    }),
  reviewBeca: (token: string, chargeId: number, decision: "approve" | "reject", note?: string) =>
    apiFetch<Ok<{ charge_id: number; beca_status: string }>>(`/superadmin/organizer-fees/becas/${chargeId}/review`, {
      method: "POST",
      token,
      body: { decision, note: note ?? null },
    }),
};

export const FEE_ESTADO_LABEL: Record<FeeEstado, string> = {
  en_curso: "En curso",
  al_corriente: "Al corriente",
  pendiente: "Pendiente",
  vencido: "Vencido",
};

export function shortDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-MX", { day: "numeric", month: "short", timeZone: "America/Monterrey" })
    .format(date)
    .replace(".", "");
}
